import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  IconArrowDown,
  IconCheckmark,
  IconCheckboxCircle,
  IconCloseCircleLine,
  IconDownload,
  IconFileDownload,
  IconLoader,
  IconSelectOnModel,
} from "@synthesis/shared-icons";
import { Button } from "@synthesis/shared-ui/Button";
import { Dropdown } from "@synthesis/shared-ui/Dropdown";
import { LoadingIndicator } from "@synthesis/shared-ui/LoadingIndicator";
import { Modal } from "@synthesis/shared-ui/Modal";
import { setSnack } from "@synthesis/shared-ui/SnackbarCenter";
import { cn, logger } from "@synthesis/shared-utils";
import {
  findByProjectIdQueryKey,
  useFindByProjectId,
  useUpdateHighlightArea,
} from "@synthesis/shared-services/models/modelsDrawingsController/hooks";
import dayjs from "dayjs";

import { downloadPdfArea, downloadPdfDirect, downloadPdfPages } from "@synthesis/shared-services";

import { usePdfCropBox, usePdfPageImage, usePdfPageObjectUrl, usePdfSidebarPreviews } from "../hooks";
import { saveBlobAsFile } from "../utils/save-blob";
import { hasDrawingRotationChanged, hasSelectionAreaChanged, SNACK } from "./add-to-collection-popup.lib";
import AddToCollectionPopup from "./add-to-collection-popup";
import CancelSelectionPopup from "./cancel-selection-popup";
import DrawingPlaneReadonly from "./drawing-plane-readonly";
import EditRepositionWarningPopup, { type EditWarningKind } from "./edit-reposition-warning-popup";
import PdfMinimap from "./pdf-minimap";
import PdfPreviewSidebar from "./pdf-preview-sidebar";
import SelectionOverlay from "./selection-overlay";
import {
  COLLECTION_ACTION,
  COLLECTION_ACTIVE_ITEM_LABELS,
  COLLECTION_SCHEME,
  COPY_PAGE_NAME_DATETIME_FORMAT,
  DEFAULT_VIEWER,
  EMPTY_SELECTION,
  INITIAL_MINIMAP_VIEWPORT,
  MODAL_MAX_WIDTH_PX,
  MODAL_MIN_HEIGHT_PX,
  MODAL_SIZE_PERCENT,
  PDF_PAGE_DPI,
  PROTECTED_PLACEMENT_STATUSES,
  UNROTATED_ANGLE,
  buildDefaultSelectionState,
  calcMinimapPanDelta,
  calcMinimapViewport,
  calcScreenRect,
  calcSelectionCoords,
  clampPan,
  clampRectToImageBounds,
  clientToStageLocal,
  getContentPanLimits,
  getDrawnSelectionRect,
  getSelectionCursor,
  getStageContentRect,
  getStageLayout,
  hasVisibleRect,
  hitTestHandle,
  hitTestPointInRect,
  isDrawnSelectionBigEnough,
  isSameRect,
  isSameSize,
  isSameViewport,
  isValidSelectionCoords,
  normalizeDrawingRotation,
  resizeRectByHandle,
  resolveViewerPageNumber,
  rotateClockwise,
  toDrawingRotationName,
  zoomInScale,
  zoomOutScale,
  type CollectionAction,
  type ContentModalProps,
  type DownloadKind,
  type DragSession,
  type DrawingPlane,
  type ModalOpenPDFProps,
  type PanSession,
  type ResizeSession,
  type SelectionCoords,
  type SelectionState,
  type ViewportMetrics,
} from "./modal-open-pdf.lib";

const ModalOpenPDF = memo(({ projectId, ...props }: ModalOpenPDFProps) => (
  <Modal.Root manual {...props}>
    <ContentModal {...props} projectId={projectId} />
  </Modal.Root>
));

const ContentModal = ({
  fileName,
  fileId,
  isOpen,
  pageCount = 0,
  mode = "default",
  initialPage,
  initialSelection,
  drawingId,
  initialName,
  initialPlane,
  initialRotation,
  projectId,
}: ContentModalProps) => {
  const queryClient = useQueryClient();
  const updateHighlightArea = useUpdateHighlightArea();
  const { data: projectDrawings = [] } = useFindByProjectId(
    { path: { projectId: projectId ?? "" } },
    {
      query: {
        enabled: isOpen && mode === "define" && !!projectId && !!drawingId,
      },
    },
  );
  const placements = useMemo(() => projectDrawings.find((item) => item.drawing?.id === drawingId)?.placements ?? [], [projectDrawings, drawingId]);
  const hasProtectedPlacements = placements.some((placement) => PROTECTED_PLACEMENT_STATUSES.includes(placement.status));
  const [collectionAction, setCollectionAction] = useState<CollectionAction>("");
  const [copyPageDefaultName, setCopyPageDefaultName] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const initialRotationValue = normalizeDrawingRotation(initialRotation);
  const [viewer, setViewer] = useState(() => ({ ...DEFAULT_VIEWER, rotation: initialRotationValue }));
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [isPanning, setIsPanning] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [isPlaneEditOpen, setIsPlaneEditOpen] = useState(false);
  const [pendingEditWarning, setPendingEditWarning] = useState<EditWarningKind | null>(null);
  const [displayPlane, setDisplayPlane] = useState<DrawingPlane | undefined>(initialPlane);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [imageNaturalSize, setImageNaturalSize] = useState({ width: 0, height: 0 });
  const [minimapViewport, setMinimapViewport] = useState(INITIAL_MINIMAP_VIEWPORT);

  const panSessionRef = useRef<PanSession>({ startX: 0, startY: 0, offsetX: 0, offsetY: 0, isMinimap: false });
  const selectSessionRef = useRef<{ x: number; y: number } | null>(null);
  const dragSessionRef = useRef<DragSession | null>(null);
  const resizeSessionRef = useRef<ResizeSession | null>(null);
  const viewportMetricsRef = useRef<ViewportMetrics>({
    containerWidth: 0,
    containerHeight: 0,
    naturalWidth: 0,
    naturalHeight: 0,
    rotation: UNROTATED_ANGLE,
  });
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const suppressZoomSyncRef = useRef(false);
  const appliedInitialSelectionRef = useRef<SelectionCoords | null>(null);
  const containerSizeRef = useRef(containerSize);
  const minimapViewportRef = useRef(INITIAL_MINIMAP_VIEWPORT);

  useEffect(() => {
    setDisplayPlane(initialPlane);
  }, [initialPlane, isOpen]);

  const handleClosePlaneEdit = () => {
    setIsPlaneEditOpen(false);
  };

  const stageMetrics = useMemo(
    () => getStageLayout(containerSize.width, containerSize.height, imageNaturalSize.width, imageNaturalSize.height),
    [containerSize.height, containerSize.width, imageNaturalSize.height, imageNaturalSize.width],
  );

  const mapClientToStage = useCallback(
    (clientX: number, clientY: number) => {
      const container = mainContainerRef.current;
      if (!container || !stageMetrics.fitW || !stageMetrics.fitH) {
        return null;
      }

      const containerRect = container.getBoundingClientRect();
      return clientToStageLocal(
        clientX,
        clientY,
        containerRect.left,
        containerRect.top,
        stageMetrics.left,
        stageMetrics.top,
        stageMetrics.fitW,
        stageMetrics.fitH,
        viewer.panX,
        viewer.panY,
        viewer.scale,
        viewer.rotation,
      );
    },
    [stageMetrics.fitH, stageMetrics.fitW, stageMetrics.left, stageMetrics.top, viewer.panX, viewer.panY, viewer.rotation, viewer.scale],
  );

  const stageContentRect = useMemo(() => getStageContentRect(stageMetrics.fitW, stageMetrics.fitH), [stageMetrics.fitH, stageMetrics.fitW]);

  const syncMinimapViewport = useCallback(() => {
    const container = mainContainerRef.current;
    const img = imgRef.current;

    if (!container) {
      return;
    }

    const { width, height } = container.getBoundingClientRect();

    viewportMetricsRef.current = {
      containerWidth: width,
      containerHeight: height,
      naturalWidth: img?.naturalWidth ?? 0,
      naturalHeight: img?.naturalHeight ?? 0,
      rotation: viewer.rotation,
    };

    if (!img?.naturalWidth || !img.naturalHeight) {
      return;
    }

    const nextViewport = calcMinimapViewport({
      containerWidth: width,
      containerHeight: height,
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight,
      scale: viewer.scale,
      panX: viewer.panX,
      panY: viewer.panY,
      rotation: viewer.rotation,
    });

    const prev = minimapViewportRef.current;
    const values = [nextViewport.left, nextViewport.top, nextViewport.width, nextViewport.height];
    if (!values.every(Number.isFinite) || isSameViewport(prev, nextViewport)) {
      return;
    }

    minimapViewportRef.current = nextViewport;
    setMinimapViewport(nextViewport);
  }, [viewer.scale, viewer.panX, viewer.panY, viewer.rotation]);

  const { pagePreviews, isLoadingPreviews, sidebarRef } = usePdfSidebarPreviews(fileId, isOpen && mode !== "define", pageCount);

  const pageNumber = resolveViewerPageNumber(mode, initialPage, pagePreviews[activeIndex]?.page, activeIndex);

  const isSelecting = collectionAction === COLLECTION_ACTION.SELECT_AREA;
  const { data: cropBox } = usePdfCropBox(fileId, pageNumber, isOpen && !!fileId && pageNumber > 0);

  const {
    data: pageBlob,
    isPending: isPageImagePending,
    isFetching: isPageImageFetching,
  } = usePdfPageImage(fileId, pageNumber, {
    enabled: isOpen && !!fileId && pageNumber > 0,
    dpi: PDF_PAGE_DPI,
  });
  const pageBlobValue = pageBlob instanceof Blob ? pageBlob : undefined;
  const pageImageSrc = usePdfPageObjectUrl(pageBlobValue);
  const isLoadingPageImage = (isPageImagePending || isPageImageFetching || !!pageBlobValue) && !pageImageSrc;

  const downloadFileMutation = useMutation({
    mutationFn: async () => {
      if (!fileId) {
        return;
      }

      const blobData = await downloadPdfDirect(fileId);
      saveBlobAsFile(blobData, fileName);
    },
    onError: (err) => {
      logger.error("Error downloading PDF:", err);
    },
  });

  const downloadPageMutation = useMutation({
    mutationFn: async () => {
      if (!fileId) {
        return;
      }

      const blobData = await downloadPdfPages(fileId, String(pageNumber), { responseType: "blob" });
      saveBlobAsFile(blobData, `${fileName}_page_${pageNumber}.pdf`);
    },
    onError: (err) => {
      logger.error("Error downloading PDF page:", err);
    },
  });

  const downloadAreaMutation = useMutation({
    mutationFn: async () => {
      if (!fileId || !selection.result) {
        return;
      }

      const blobData = await downloadPdfArea(fileId, {
        page: pageNumber,
        originX: selection.result.originX,
        originY: selection.result.originY,
        areaWidth: selection.result.areaWidth,
        areaHeight: selection.result.areaHeight,
        rotation: toDrawingRotationName(viewer.rotation),
      });
      saveBlobAsFile(blobData, `${fileName}_area.pdf`);
    },
    onError: (err) => {
      logger.error("Error downloading PDF area:", err);
      setSnack({
        title: "Не удалось скачать выделенную область",
        messageType: "error",
      });
    },
  });

  const downloadKind: DownloadKind | null = downloadFileMutation.isPending
    ? "file"
    : downloadPageMutation.isPending
      ? "page"
      : downloadAreaMutation.isPending
        ? "area"
        : null;

  const handleCopyPageToCollection = () => {
    selectSessionRef.current = null;
    dragSessionRef.current = null;
    resizeSessionRef.current = null;
    setSelection(EMPTY_SELECTION);
    setCopyPageDefaultName(`${fileName}_page_${pageNumber}_${dayjs().format(COPY_PAGE_NAME_DATETIME_FORMAT)}`);
    setCollectionAction(COLLECTION_ACTION.COPY_PAGE);
  };

  const handleCloseCopyPagePopup = () => {
    setCollectionAction("");
    setCopyPageDefaultName("");
  };

  const collectionDropdownState = {
    [COLLECTION_ACTION.SELECT_AREA]: () => setCollectionAction(COLLECTION_ACTION.SELECT_AREA),
    [COLLECTION_ACTION.COPY_PAGE]: handleCopyPageToCollection,
  };

  const handleCloseSelectAreaPopup = () => {
    setCollectionAction("");
    selectSessionRef.current = null;
    dragSessionRef.current = null;
    resizeSessionRef.current = null;

    setSelection((prev) => {
      if (!prev.rect && !prev.result) {
        return EMPTY_SELECTION;
      }

      let result = prev.result;

      if (!result && prev.rect && cropBox && stageMetrics.fitW && stageMetrics.fitH) {
        result = calcSelectionCoords(prev.rect, stageContentRect, cropBox);
      }

      if (!result) {
        return EMPTY_SELECTION;
      }

      return {
        rect: prev.rect,
        result,
        isModalOpen: false,
        isActive: false,
      };
    });
  };

  const resetSelectionToDefault = () => {
    setCollectionAction("");
    selectSessionRef.current = null;
    dragSessionRef.current = null;
    resizeSessionRef.current = null;
    suppressZoomSyncRef.current = false;
    setIsCancelConfirmOpen(false);
    setSelection(buildDefaultSelectionState(initialSelection));
  };

  const handleDismissCollectionPopup = () => {
    setSelection((prev) => {
      if (!prev.result) {
        return EMPTY_SELECTION;
      }

      return {
        ...prev,
        isModalOpen: false,
        isActive: true,
        rect: null,
      };
    });
  };

  const handleEditArea = () => {
    if (!cropBox) {
      return;
    }

    setCollectionAction(COLLECTION_ACTION.SELECT_AREA);

    setSelection((prev) => {
      const source = isValidSelectionCoords(prev.result) ? prev.result : isValidSelectionCoords(initialSelection) ? initialSelection : null;

      if (!source) {
        return prev;
      }

      const canPlaceRect = !!cropBox && stageMetrics.fitW > 0 && stageMetrics.fitH > 0;
      const rect = hasVisibleRect(prev.rect) ? prev.rect : canPlaceRect ? calcScreenRect(source, stageContentRect, cropBox) : null;

      // Continue from the currently visible area (after Cancel), not the original saved one.
      return {
        result: { ...source },
        rect,
        isModalOpen: false,
        isActive: true,
      };
    });
  };

  const requestEditArea = () => {
    if (hasProtectedPlacements) {
      setPendingEditWarning("area");
      return;
    }

    handleEditArea();
  };

  const requestEditPlane = () => {
    if (hasProtectedPlacements) {
      setPendingEditWarning("plane");
      return;
    }

    setIsPlaneEditOpen(true);
  };

  const confirmPendingEdit = () => {
    const kind = pendingEditWarning;
    setPendingEditWarning(null);

    if (kind === "area") {
      handleEditArea();
    } else if (kind === "plane") {
      setIsPlaneEditOpen(true);
    }
  };

  const handleConfirm = async () => {
    if (!cropBox || !stageMetrics.fitW || !stageMetrics.fitH || !selection.rect || updateHighlightArea.isPending) {
      return;
    }

    const result = calcSelectionCoords(selection.rect, stageContentRect, cropBox);

    if (mode !== "define") {
      setSelection({ rect: null, result, isModalOpen: true, isActive: false });
      return;
    }

    if (!projectId) {
      setSnack({ title: SNACK.projectNotFound, messageType: "error" });
      return;
    }

    if (!drawingId) {
      setSnack({ title: SNACK.drawingNotFound, messageType: "error" });
      return;
    }

    const finishAreaEdit = () => {
      setCollectionAction("");
      selectSessionRef.current = null;
      dragSessionRef.current = null;
      resizeSessionRef.current = null;
      setSelection({
        rect: selection.rect,
        result,
        isModalOpen: false,
        isActive: false,
      });
    };

    const rotationChanged = hasDrawingRotationChanged(viewer.rotation, initialRotation);

    if (!hasSelectionAreaChanged(result, initialSelection) && !rotationChanged) {
      finishAreaEdit();
      return;
    }

    try {
      await updateHighlightArea.mutateAsync({
        path: { drawingId },
        body: {
          originX: result.originX,
          originY: result.originY,
          areaWidth: result.areaWidth,
          areaHeight: result.areaHeight,
          rotation: toDrawingRotationName(viewer.rotation),
        },
      });
      await queryClient.invalidateQueries({ queryKey: findByProjectIdQueryKey({ path: { projectId } }) });
      setSnack({
        title: SNACK.areaUpdated,
        messageType: "info",
        icon: <IconCheckmark className="size-5" />,
      });
      finishAreaEdit();
    } catch {
      setSnack({
        title: SNACK.drawingUpdateFailed,
        messageType: "error",
      });
    }
  };

  const handleZoomIn = () => setViewer((prev) => ({ ...prev, scale: zoomInScale(prev.scale) }));
  const handleZoomOut = () => setViewer((prev) => ({ ...prev, scale: zoomOutScale(prev.scale) }));
  const hiddenLeftBar = () => setViewer((prev) => ({ ...prev, isLeftPanelHidden: !prev.isLeftPanelHidden }));
  const handleRotate = () =>
    setViewer((prev) => ({
      ...prev,
      rotation: rotateClockwise(prev.rotation),
      panX: 0,
      panY: 0,
    }));

  const handleDownload = () => {
    if (!fileId || downloadKind) {
      return;
    }

    downloadFileMutation.mutate();
  };

  const handleDownloadPage = () => {
    if (!fileId || downloadKind) {
      return;
    }

    downloadPageMutation.mutate();
  };

  const handleDownloadArea = () => {
    if (!fileId || !selection.result || downloadKind) {
      return;
    }

    downloadAreaMutation.mutate();
  };

  const clampOffset = useCallback(
    (nextX: number, nextY: number) => {
      const container = mainContainerRef.current;
      const img = imgRef.current;
      if (!container || !img?.naturalWidth || !img.naturalHeight || viewer.scale <= 1) {
        return { x: 0, y: 0 };
      }

      const { width, height } = container.getBoundingClientRect();
      const { contentMaxPanX: maxX, contentMaxPanY: maxY } = getContentPanLimits(
        width,
        height,
        img.naturalWidth,
        img.naturalHeight,
        viewer.scale,
        viewer.rotation,
      );
      return clampPan(nextX, nextY, maxX, maxY);
    },
    [viewer.scale, viewer.rotation],
  );

  const startDrag = useCallback(
    (e: ReactMouseEvent, isMinimap: boolean) => {
      e.preventDefault();
      setIsPanning(true);
      panSessionRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        offsetX: viewer.panX,
        offsetY: viewer.panY,
        isMinimap,
      };
    },
    [viewer.panX, viewer.panY],
  );

  const onPanMove = useCallback(
    (e: MouseEvent) => {
      if (!isPanning) {
        return;
      }

      const { startX, startY, offsetX, offsetY, isMinimap } = panSessionRef.current;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const metrics = viewportMetricsRef.current;

      const delta =
        isMinimap && metrics.containerWidth > 0 && metrics.naturalWidth > 0
          ? calcMinimapPanDelta({
              dx,
              dy,
              containerWidth: metrics.containerWidth,
              containerHeight: metrics.containerHeight,
              naturalWidth: metrics.naturalWidth,
              naturalHeight: metrics.naturalHeight,
              scale: viewer.scale,
              rotation: metrics.rotation,
            })
          : { deltaX: dx, deltaY: dy };

      const next = clampOffset(offsetX + delta.deltaX, offsetY + delta.deltaY);
      setViewer((prev) => ({ ...prev, panX: next.x, panY: next.y }));
    },
    [isPanning, viewer.scale, clampOffset],
  );

  const onPanUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  const handleMainMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      if (!isSelecting || !mainContainerRef.current) {
        startDrag(e, false);
        return;
      }

      if (!cropBox) {
        return;
      }

      e.preventDefault();
      const local = mapClientToStage(e.clientX, e.clientY);
      if (!local) {
        return;
      }

      const { x, y } = local;

      // If an active selection exists, check handles first, then body
      if (selection.isActive && selection.rect) {
        const handle = hitTestHandle(x, y, selection.rect);
        if (handle) {
          resizeSessionRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            rectX: selection.rect.x,
            rectY: selection.rect.y,
            rectW: selection.rect.w,
            rectH: selection.rect.h,
            handle,
          };
          selectSessionRef.current = null;
          dragSessionRef.current = null;
          return;
        }

        if (hitTestPointInRect(x, y, selection.rect)) {
          dragSessionRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            rectX: selection.rect.x,
            rectY: selection.rect.y,
          };
          selectSessionRef.current = null;
          resizeSessionRef.current = null;
          return;
        }
      }

      // Start new selection
      selectSessionRef.current = { x, y };
      dragSessionRef.current = null;
      resizeSessionRef.current = null;
      setSelection((prev) => ({ ...prev, rect: { x, y, w: 0, h: 0 }, isActive: false }));
    },
    [cropBox, isSelecting, startDrag, selection.isActive, selection.rect, mapClientToStage],
  );

  const handleMainMouseMove = useCallback(
    (e: MouseEvent) => {
      // Resize / drag / draw — work in stage-local space so the rect stays glued to the PDF image.
      if (resizeSessionRef.current && stageMetrics.fitW && stageMetrics.fitH) {
        const startLocal = mapClientToStage(resizeSessionRef.current.startX, resizeSessionRef.current.startY);
        const currentLocal = mapClientToStage(e.clientX, e.clientY);
        if (!startLocal || !currentLocal) {
          return;
        }

        const { rectX, rectY, rectW, rectH, handle } = resizeSessionRef.current;
        const dx = currentLocal.x - startLocal.x;
        const dy = currentLocal.y - startLocal.y;
        const next = resizeRectByHandle({ x: rectX, y: rectY, w: rectW, h: rectH }, handle, dx, dy);
        const clamped = clampRectToImageBounds(next, stageContentRect);
        setSelection((prev) => ({ ...prev, rect: clamped, isActive: true }));
        return;
      }

      if (dragSessionRef.current && stageMetrics.fitW && stageMetrics.fitH) {
        const startLocal = mapClientToStage(dragSessionRef.current.startX, dragSessionRef.current.startY);
        const currentLocal = mapClientToStage(e.clientX, e.clientY);
        const currentRect = selection.rect;
        if (!startLocal || !currentLocal || !currentRect) {
          return;
        }

        const dx = currentLocal.x - startLocal.x;
        const dy = currentLocal.y - startLocal.y;
        const { rectX, rectY } = dragSessionRef.current;
        const clamped = clampRectToImageBounds({ x: rectX + dx, y: rectY + dy, w: currentRect.w, h: currentRect.h }, stageContentRect);
        setSelection((prev) => ({ ...prev, rect: clamped, isActive: true }));
        return;
      }

      if (isSelecting && selectSessionRef.current) {
        const local = mapClientToStage(e.clientX, e.clientY);
        if (!local) {
          return;
        }

        const start = selectSessionRef.current;
        const next = clampRectToImageBounds(getDrawnSelectionRect(start, local), stageContentRect);
        setSelection((prev) => ({
          ...prev,
          rect: next,
          isActive: false,
        }));
        return;
      }

      onPanMove(e);
    },
    [isSelecting, mapClientToStage, onPanMove, selection.rect, stageContentRect, stageMetrics.fitH, stageMetrics.fitW],
  );

  const handleMainMouseUp = useCallback(() => {
    if (resizeSessionRef.current) {
      resizeSessionRef.current = null;
      if (cropBox && stageMetrics.fitW && stageMetrics.fitH && selection.rect) {
        const result = calcSelectionCoords(selection.rect, stageContentRect, cropBox);
        suppressZoomSyncRef.current = true;
        setSelection((prev) => ({ ...prev, result }));
      }
      return;
    }

    if (dragSessionRef.current) {
      dragSessionRef.current = null;
      if (cropBox && stageMetrics.fitW && stageMetrics.fitH && selection.rect) {
        const result = calcSelectionCoords(selection.rect, stageContentRect, cropBox);
        suppressZoomSyncRef.current = true;
        setSelection((prev) => ({ ...prev, result }));
      }
      return;
    }

    if (isSelecting && selectSessionRef.current && selection.rect) {
      if (isDrawnSelectionBigEnough(selection.rect)) {
        if (cropBox && stageMetrics.fitW && stageMetrics.fitH) {
          const result = calcSelectionCoords(selection.rect, stageContentRect, cropBox);
          suppressZoomSyncRef.current = true;
          setSelection((prev) => ({ ...prev, result, isActive: true }));
        } else {
          setSelection((prev) => ({ ...prev, isActive: true }));
        }
      } else {
        setSelection(EMPTY_SELECTION);
      }
      selectSessionRef.current = null;
      return;
    }

    onPanUp();
  }, [cropBox, isSelecting, onPanUp, selection.rect, stageContentRect, stageMetrics.fitH, stageMetrics.fitW]);

  const handlePageImageLoad = useCallback(
    (img: HTMLImageElement) => {
      if (!img.naturalWidth || !img.naturalHeight) {
        return;
      }

      setImageNaturalSize((prev) =>
        prev.width === img.naturalWidth && prev.height === img.naturalHeight ? prev : { width: img.naturalWidth, height: img.naturalHeight },
      );
      syncMinimapViewport();
    },
    [syncMinimapViewport],
  );

  // Places the selection rect directly from live DOM measurements when the page image
  // is mounted. This bypasses the stageMetrics/containerSize state chain (which may be
  // stale when the image loads instantly from cache), restoring the area on reopen.
  const placeSelectionRectFromDom = useCallback(
    (img: HTMLImageElement) => {
      if (!img.naturalWidth || !img.naturalHeight) {
        return;
      }

      const container = mainContainerRef.current;

      if (!container) {
        return;
      }

      const { width: cw, height: ch } = container.getBoundingClientRect();

      if (!cw || !ch) {
        return;
      }

      const { fitW, fitH } = getStageLayout(cw, ch, img.naturalWidth, img.naturalHeight);

      if (!fitW || !fitH) {
        return;
      }

      const contentRect = { x: 0, y: 0, w: fitW, h: fitH };

      setSelection((prev) => {
        if (!prev.result || prev.isModalOpen) {
          return prev;
        }

        if (!cropBox) {
          return prev;
        }

        const newRect = calcScreenRect(prev.result, contentRect, cropBox);

        if (newRect.w <= 0 || newRect.h <= 0) {
          return prev;
        }

        if (isSameRect(prev.rect, newRect)) {
          return prev;
        }

        return { ...prev, rect: newRect, isActive: prev.isActive || isSelecting };
      });
    },
    [cropBox, isSelecting],
  );

  useEffect(() => {
    const needsGlobal = isPanning || isSelecting;

    if (!needsGlobal) {
      return;
    }

    window.addEventListener("mousemove", handleMainMouseMove);
    window.addEventListener("mouseup", handleMainMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMainMouseMove);
      window.removeEventListener("mouseup", handleMainMouseUp);
    };
  }, [isPanning, isSelecting, handleMainMouseMove, handleMainMouseUp]);

  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(0);
      setCollectionAction("");
      setCopyPageDefaultName("");
      setViewer({ ...DEFAULT_VIEWER, rotation: initialRotationValue });
      setSelection(EMPTY_SELECTION);
      setImageNaturalSize({ width: 0, height: 0 });
      setIsPanning(false);
      setIsCancelConfirmOpen(false);
      setIsPlaneEditOpen(false);
      setPendingEditWarning(null);
      selectSessionRef.current = null;
      dragSessionRef.current = null;
      resizeSessionRef.current = null;
      suppressZoomSyncRef.current = false;
    }
  }, [initialRotationValue, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setViewer((prev) => (prev.rotation === initialRotationValue ? prev : { ...prev, rotation: initialRotationValue, panX: 0, panY: 0 }));
  }, [initialRotationValue, isOpen]);

  useEffect(() => {
    if (!isOpen || mode !== "define" || !isValidSelectionCoords(initialSelection)) {
      appliedInitialSelectionRef.current = null;
      return;
    }

    const applied = appliedInitialSelectionRef.current;

    if (
      applied &&
      applied.originX === initialSelection.originX &&
      applied.originY === initialSelection.originY &&
      applied.areaWidth === initialSelection.areaWidth &&
      applied.areaHeight === initialSelection.areaHeight
    ) {
      return;
    }

    appliedInitialSelectionRef.current = { ...initialSelection };

    setSelection({
      result: { ...initialSelection },
      rect: null,
      isModalOpen: false,
      isActive: false,
    });
  }, [isOpen, initialSelection, mode]);

  useEffect(() => {
    setViewer((prev) => ({ ...prev, panX: 0, panY: 0 }));

    if (mode === "define") {
      return;
    }

    setCollectionAction("");
    setSelection(EMPTY_SELECTION);
    selectSessionRef.current = null;
    dragSessionRef.current = null;
    resizeSessionRef.current = null;
  }, [activeIndex, mode]);

  useEffect(() => {
    setViewer((prev) => {
      const next = clampOffset(prev.panX, prev.panY);
      if (next.x === prev.panX && next.y === prev.panY) {
        return prev;
      }

      return { ...prev, panX: next.x, panY: next.y };
    });
  }, [viewer.scale, viewer.rotation, clampOffset]);

  useEffect(() => {
    if (!selection.result || selection.isModalOpen || !cropBox || !stageMetrics.fitW || !stageMetrics.fitH) {
      return;
    }
    if (dragSessionRef.current || resizeSessionRef.current || selectSessionRef.current) {
      return;
    }

    const hasVisibleArea = hasVisibleRect(selection.rect);
    if (suppressZoomSyncRef.current) {
      suppressZoomSyncRef.current = false;
      // Still place the rect when restoring a saved area (rect is null until stage is ready).
      if (hasVisibleArea) {
        return;
      }
    }

    // Stage-local rect is derived from PDF points × current stage size — independent of viewport chrome.
    const newRect = calcScreenRect(selection.result, stageContentRect, cropBox);
    if (newRect.w <= 0 || newRect.h <= 0) {
      return;
    }

    setSelection((prev) => {
      if (!prev.result || prev.isModalOpen) {
        return prev;
      }
      if (isSameRect(prev.rect, newRect)) {
        return prev;
      }
      return { ...prev, rect: newRect, isActive: prev.isActive || isSelecting };
    });
  }, [
    containerSize.height,
    containerSize.width,
    cropBox,
    isSelecting,
    pageImageSrc,
    selection.isModalOpen,
    selection.rect,
    selection.result,
    stageContentRect,
    stageMetrics.fitH,
    stageMetrics.fitW,
  ]);

  const activePreview = pagePreviews[activeIndex];
  const showPageImage = !isLoadingPageImage && !!pageImageSrc;
  const hasStageSize = stageMetrics.fitW > 0 && stageMetrics.fitH > 0;

  useLayoutEffect(() => {
    const container = mainContainerRef.current;
    if (!container || !isOpen) {
      return;
    }

    const updateSize = () => {
      const { width, height } = container.getBoundingClientRect();
      const prev = containerSizeRef.current;

      if (isSameSize(prev, { width, height })) {
        return;
      }

      const next = { width, height };
      containerSizeRef.current = next;
      setContainerSize(next);
    };

    updateSize();
    // Dialog layout may settle after the first paint on reopen — measure again next frame.
    const rafId = window.requestAnimationFrame(updateSize);
    const observer = new ResizeObserver(updateSize);
    observer.observe(container);

    return () => {
      window.cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [isOpen, pageImageSrc, syncMinimapViewport, imageNaturalSize.width, imageNaturalSize.height]);

  useLayoutEffect(() => {
    syncMinimapViewport();
  }, [syncMinimapViewport, pageImageSrc, isLoadingPageImage, containerSize.width, containerSize.height]);

  useLayoutEffect(() => {
    if (!pageImageSrc) {
      setImageNaturalSize({ width: 0, height: 0 });
      return;
    }

    const img = imgRef.current;

    if (img?.complete && img.naturalWidth && img.naturalHeight) {
      setImageNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
      return;
    }

    // Only reset metrics when the object URL itself changes and the new image is not ready yet.
    setImageNaturalSize({ width: 0, height: 0 });
  }, [pageImageSrc]);

  useLayoutEffect(() => {
    if (!showPageImage || !pageImageSrc) {
      return;
    }

    const img = imgRef.current;

    if (img?.complete && img.naturalWidth && img.naturalHeight) {
      handlePageImageLoad(img);
      placeSelectionRectFromDom(img);
    }
  }, [handlePageImageLoad, placeSelectionRectFromDom, pageImageSrc, showPageImage]);

  const selectionCursor = useMemo(
    () => getSelectionCursor(isSelecting, isPanning, !!dragSessionRef.current, resizeSessionRef.current?.handle ?? null),
    [isSelecting, isPanning],
  );

  return (
    <Modal.Content
      className="flex flex-col"
      style={{
        height: `${MODAL_SIZE_PERCENT}vh`,
        minHeight: MODAL_MIN_HEIGHT_PX,
        width: `${MODAL_SIZE_PERCENT}vw`,
        maxWidth: MODAL_MAX_WIDTH_PX,
      }}
    >
      <Modal.Title>{fileName}</Modal.Title>
      <div className="relative flex min-h-0 w-full flex-1">
        {(selection.isModalOpen && selection.result) || collectionAction === COLLECTION_ACTION.COPY_PAGE || isPlaneEditOpen ? (
          <AddToCollectionPopup
            onClose={
              isPlaneEditOpen
                ? handleClosePlaneEdit
                : collectionAction === COLLECTION_ACTION.COPY_PAGE
                  ? handleCloseCopyPagePopup
                  : handleCloseSelectAreaPopup
            }
            onCancel={
              isPlaneEditOpen
                ? handleClosePlaneEdit
                : collectionAction === COLLECTION_ACTION.COPY_PAGE
                  ? handleCloseCopyPagePopup
                  : handleDismissCollectionPopup
            }
            onUpdated={({ plane }) => {
              if (plane) {
                setDisplayPlane(plane);
              }
            }}
            selectionCoords={isPlaneEditOpen || collectionAction === COLLECTION_ACTION.COPY_PAGE ? null : selection.result}
            initialSelection={initialSelection}
            fileId={fileId ?? ""}
            pageNumber={pageNumber}
            mode={isPlaneEditOpen || mode === "define" ? "define" : mode}
            variant={isPlaneEditOpen ? "planeOnly" : "default"}
            drawingId={drawingId}
            initialName={collectionAction === COLLECTION_ACTION.COPY_PAGE ? copyPageDefaultName : initialName}
            initialPlane={displayPlane ?? initialPlane}
            rotation={viewer.rotation}
            projectId={projectId}
          />
        ) : null}
        {isCancelConfirmOpen ? <CancelSelectionPopup onConfirm={resetSelectionToDefault} onCancel={() => setIsCancelConfirmOpen(false)} /> : null}
        {pendingEditWarning ? (
          <EditRepositionWarningPopup kind={pendingEditWarning} onConfirm={confirmPendingEdit} onCancel={() => setPendingEditWarning(null)} />
        ) : null}

        {mode !== "define" && !viewer.isLeftPanelHidden && (
          <PdfPreviewSidebar
            sidebarRef={sidebarRef}
            pagePreviews={pagePreviews}
            isLoadingPreviews={isLoadingPreviews}
            activeIndex={activeIndex}
            onSelectPage={setActiveIndex}
          />
        )}

        <div className="relative flex h-full min-w-0 flex-1 flex-col overflow-hidden">
          <div className="bg-neutrals-99 w-full shrink-0">
            <div className="border-neutrals-90 flex w-full items-center justify-between border-b-[1px] px-4 py-3">
              <div className="flex items-center gap-2">
                {mode === "define" ? (
                  <Button
                    size="sm"
                    theme="secondary"
                    leftIcon={<IconSelectOnModel className="size-5" />}
                    onClick={requestEditArea}
                    disabled={!cropBox}
                  >
                    Изменить область
                  </Button>
                ) : (
                  <Dropdown
                    scheme={COLLECTION_SCHEME}
                    state={collectionDropdownState}
                    activeItem={collectionAction ? COLLECTION_ACTIVE_ITEM_LABELS[collectionAction] : undefined}
                    size="sm"
                    portal={false}
                    trigger={
                      <Button size="sm" theme="text" rightIcon={<IconArrowDown className="size-5" />}>
                        {collectionAction === COLLECTION_ACTION.SELECT_AREA
                          ? COLLECTION_ACTIVE_ITEM_LABELS[COLLECTION_ACTION.SELECT_AREA]
                          : "Добавить в коллекцию"}
                      </Button>
                    }
                  />
                )}
                {isSelecting && (
                  <>
                    <Button
                      leftIcon={<IconCheckboxCircle className="size-5" />}
                      size="sm"
                      theme="text"
                      onClick={() => void handleConfirm()}
                      disabled={!hasVisibleRect(selection.rect) || updateHighlightArea.isPending}
                    >
                      Подтвердить
                    </Button>
                    <Button
                      leftIcon={<IconCloseCircleLine className="size-5" />}
                      size="sm"
                      theme="text"
                      onClick={() => setIsCancelConfirmOpen(true)}
                      disabled={
                        !hasVisibleRect(selection.rect) && !isValidSelectionCoords(selection.result) && !isValidSelectionCoords(initialSelection)
                      }
                    >
                      Отменить
                    </Button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                {mode === "define" ? (
                  <>
                    <DrawingPlaneReadonly key={displayPlane} value={displayPlane} />
                    <Button size="sm" theme="text" onClick={requestEditPlane}>
                      Изменить плоскость
                    </Button>
                  </>
                ) : null}
                {mode !== "define" && (
                  <Button
                    size="sm"
                    theme="text"
                    className={cn(downloadKind && "cursor-not-allowed")}
                    leftIcon={
                      downloadKind === "file" ? <IconLoader className="size-5 animate-spin" aria-hidden /> : <IconDownload className="size-5" />
                    }
                    onClick={handleDownload}
                    disabled={!!downloadKind}
                  >
                    Скачать
                  </Button>
                )}
                {mode === "define" && (
                  <Button
                    size="sm"
                    theme="text"
                    className={cn(downloadKind && "cursor-not-allowed")}
                    leftIcon={
                      downloadKind === "area" ? <IconLoader className="size-5 animate-spin" aria-hidden /> : <IconDownload className="size-5" />
                    }
                    onClick={handleDownloadArea}
                    disabled={!selection.result || !!downloadKind}
                  >
                    Скачать выделенную область
                  </Button>
                )}
                <Button
                  size="sm"
                  theme="text"
                  className={cn(downloadKind && "cursor-not-allowed")}
                  leftIcon={
                    downloadKind === "page" ? <IconLoader className="size-5 animate-spin" aria-hidden /> : <IconFileDownload className="size-5" />
                  }
                  onClick={handleDownloadPage}
                  disabled={!!downloadKind}
                >
                  Скачать страницу
                </Button>
              </div>
            </div>
          </div>

          <div
            ref={mainContainerRef}
            className="bg-neutrals-95 relative flex min-h-0 flex-1 items-center justify-center overflow-hidden"
            style={{ cursor: selectionCursor }}
            onMouseDown={handleMainMouseDown}
          >
            {(isLoadingPageImage || (isLoadingPreviews && pagePreviews.length === 0)) && (
              <div className="absolute inset-0 z-20 flex items-center justify-center">
                <LoadingIndicator />
              </div>
            )}
            {showPageImage && (
              <div
                className="absolute"
                style={
                  hasStageSize
                    ? {
                        left: stageMetrics.left,
                        top: stageMetrics.top,
                        width: stageMetrics.fitW,
                        height: stageMetrics.fitH,
                        transform: `translate(${viewer.panX}px, ${viewer.panY}px) scale(${viewer.scale}) rotate(${viewer.rotation}deg)`,
                        transformOrigin: "center center",
                      }
                    : {
                        inset: 0,
                      }
                }
              >
                <img
                  key={pageImageSrc}
                  ref={imgRef}
                  src={pageImageSrc}
                  alt="IMG preview"
                  onLoad={(e) => {
                    handlePageImageLoad(e.currentTarget);
                    placeSelectionRectFromDom(e.currentTarget);
                  }}
                  className={cn("pointer-events-none h-full w-full max-w-none select-none", !hasStageSize && "object-contain")}
                  draggable={false}
                />
                {hasStageSize && selection.rect && selection.rect.w > 0 && selection.rect.h > 0 && (
                  <SelectionOverlay rect={selection.rect} isActive={selection.isActive} />
                )}
              </div>
            )}
            {!isLoadingPreviews && !isLoadingPageImage && !pageImageSrc && <div className="text-type-minor">Не удалось загрузить превью PDF</div>}
          </div>

          <PdfMinimap
            mode={mode}
            isLeftPanelHidden={viewer.isLeftPanelHidden}
            rotation={viewer.rotation}
            scale={viewer.scale}
            viewport={minimapViewport}
            previewSrc={activePreview?.img || pageImageSrc || undefined}
            onToggleLeftPanel={hiddenLeftBar}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onRotate={handleRotate}
            onMinimapMouseDown={(event) => startDrag(event, true)}
          />
        </div>
      </div>
    </Modal.Content>
  );
};

export { ModalOpenPDF };
export default ModalOpenPDF;
